// Informational banner shown when the boot flow resolved the URL as a shared
// puzzle. Reads sender name + their solve time from the decoded share so the
// recipient sees "Shared by Alice — solved in 04:23". Purely informational;
// doesn't gate any input. Auto-dismisses on first user input OR after a few
// seconds, and is closable with an X button.

import { el } from '@bn-games/ui-shell';
import { t } from '../i18n/I18n.js';
import { formatTime } from './settingsRows.js';

const AUTO_DISMISS_MS = 8000;
const DISMISS_EVENTS: readonly string[] = ['pointerdown', 'keydown'];

export interface SharedPuzzleBannerOptions {
  readonly from: string | null;
  readonly timeMs: number | null;
}

export interface SharedPuzzleBannerHandle {
  readonly dismiss: () => void;
}

/**
 * Mount the banner on `document.body` and return a handle that lets the caller
 * dismiss it eagerly. The banner auto-dismisses on the first pointer/keyboard
 * event or after `AUTO_DISMISS_MS`. If neither `from` nor `timeMs` is present,
 * the banner is a no-op (returns a handle whose `dismiss` is a noop).
 */
export function mountSharedPuzzleBanner(
  opts: SharedPuzzleBannerOptions,
): SharedPuzzleBannerHandle {
  if (opts.from === null && opts.timeMs === null) {
    return { dismiss: () => {} };
  }

  const fromLabel = opts.from ?? '';
  const timeLabel = opts.timeMs === null ? '' : formatTime(opts.timeMs);

  const message = t('share.banner.text', { from: fromLabel, time: timeLabel });

  const text = el('span', { class: 'sudoku-share-banner__text', text: message });
  const closeBtn = el('button', {
    class: 'sudoku-share-banner__close',
    text: '×',
    attrs: { type: 'button', 'aria-label': t('common.cancel') },
  });
  const banner = el(
    'div',
    { class: 'sudoku-share-banner', attrs: { role: 'status', 'aria-live': 'polite' } },
    [text, closeBtn],
  );

  let dismissed = false;
  let timer: number | null = null;

  function cleanup(): void {
    if (dismissed) return;
    dismissed = true;
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
    for (const evt of DISMISS_EVENTS) {
      window.removeEventListener(evt, onUserInput, true);
    }
    banner.remove();
  }

  function onUserInput(): void {
    cleanup();
  }

  closeBtn.addEventListener('click', () => cleanup());
  for (const evt of DISMISS_EVENTS) {
    window.addEventListener(evt, onUserInput, { capture: true, once: true });
  }
  timer = window.setTimeout(() => cleanup(), AUTO_DISMISS_MS);

  document.body.appendChild(banner);

  return { dismiss: cleanup };
}
